import React, { useCallback, useContext, useEffect, useState } from "react";

import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessagesList from "../MessagesList";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { format, parseISO } from "date-fns";

/** Largura do conteúdo = largura A4 em px (210mm) para ocupar 100% da página */
const PAGE_CONTENT_WIDTH = 840;
/** Altura máxima por chunk (proporção A4 210:297) */
const MAX_CHUNK_HEIGHT_PX = 1188;

/** Aguarda as imagens do container carregarem (ou timeout) para o PDF não sair em branco */
const waitForImages = (container, timeoutMs = 4000) => {
  const imgs = container.querySelectorAll("img");
  return Promise.all(
    Array.from(imgs).map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, timeoutMs);
        })
    )
  );
};

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

export default function TicketMessagesDialog({ open, handleClose, ticketId, exportMode = "screen", ticket: ticketProp }) {
  const history = useHistory();
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);

  const [, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  const [exportedToPDF, setExportedToPDF] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  /** Busca todas as mensagens do ticket (paginação). A API retorna páginas da mais nova para a mais antiga; montamos ordem cronológica (mais antiga primeiro) para o PDF iniciar do início da conversa. */
  const fetchAllMessages = async () => {
    let all = [];
    let pageNumber = 1;
    let hasMore = true;
    while (hasMore) {
      const { data } = await api.get("/messages/" + ticketId, {
        params: { pageNumber: String(pageNumber) },
      });
      const pageMessages = data.messages && data.messages.length ? data.messages : [];
      all = pageMessages.concat(all);
      hasMore = data.hasMore === true;
      pageNumber++;
    }
    return all;
  };

  /** Busca apenas a primeira página (o que está em tela) para export "Apenas o que está em tela" */
  const fetchFirstPageMessages = async () => {
    const { data } = await api.get("/messages/" + ticketId, {
      params: { pageNumber: "1" },
    });
    return (data.messages && data.messages.length) ? data.messages : [];
  };

  /** URL completa da mídia (backend ou absoluta) */
  const getMediaFullUrl = (mediaUrl) => {
    if (!mediaUrl) return "";
    if (typeof mediaUrl === "string" && mediaUrl.startsWith("http")) return mediaUrl;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return base + (mediaUrl.startsWith("/") ? mediaUrl : "/" + mediaUrl);
  };

  /** Verifica se a mensagem é imagem (para exibir no PDF) */
  const isImageMessage = (msg) => {
    const t = (msg.mediaType || "").toLowerCase();
    return t === "image" || t === "imagem" || t === "image/jpeg" || t === "image/png" || t === "image/gif" || t === "image/webp";
  };

  /** Verifica se é PDF/documento (para colocar URL clicável) */
  const isPdfOrDocumentMessage = (msg) => {
    const t = (msg.mediaType || "").toLowerCase();
    const url = (msg.mediaUrl || "").toLowerCase();
    return t === "application/pdf" || t === "pdf" || t === "document" || t === "documento" || url.endsWith(".pdf");
  };

  /** Verifica se é áudio (para colocar URL clicável) */
  const isAudioMessage = (msg) => {
    const t = (msg.mediaType || "").toLowerCase();
    const url = (msg.mediaUrl || "").toLowerCase();
    return t === "audio" || t === "audio/ogg" || t === "audio/mpeg" || t === "audio/mp3" || t === "audio/mp4" || /\.(ogg|mp3|m4a|wav|aac)$/i.test(url);
  };

  /** Label para mídia (PDF, áudio ou genérico) */
  const getMediaLabel = (msg) => {
    if (isPdfOrDocumentMessage(msg)) return "📄 Documento PDF";
    if (isAudioMessage(msg)) return "🎵 Áudio";
    return "📎 Arquivo";
  };

  /** Barra separadora #Chamado (igual ao ticket). Número e departamento sempre visíveis (innerHTML para html2canvas). */
  const createChamadoSeparatorNode = (ticketIdVal, queueName) => {
    const wrap = document.createElement("div");
    wrap.style.cssText = "width:100%;margin:10px 0;box-sizing:border-box;";
    const bar = document.createElement("div");
    bar.style.cssText = "width:100%;padding:10px 12px;border-radius:8px;background:#1976d2;box-sizing:border-box;min-height:24px;";
    const idNum = ticketIdVal !== undefined && ticketIdVal !== null && ticketIdVal !== "" ? String(ticketIdVal) : String(ticketId ?? "—");
    const dept = queueName && String(queueName).trim() ? String(queueName).trim() : "Sem Fila";
    bar.innerHTML = `<span style="color:#ffffff;font-size:14px;font-weight:bold;font-family:Arial,sans-serif;">#Chamado ${idNum} - ${dept}</span>`;
    wrap.appendChild(bar);
    return wrap;
  };

  /** Normaliza fromMe (API pode vir 0/1, true/false ou "0"/"1"/"true"/"false"). */
  const isFromMe = (msg) => {
    const v = msg.fromMe;
    if (v === true || v === 1) return true;
    if (v === false || v === 0) return false;
    const s = String(v).toLowerCase();
    return s === "true" || s === "1";
  };

  /** Linha igual ao ticket: atendente à direita (bolha verde), contato à esquerda (bolha cinza). Flex para alinhamento confiável no PDF. */
  const messageToHtmlNode = (msg) => {
    const fromMe = isFromMe(msg);
    const row = document.createElement("div");
    row.style.cssText = "width:100%;display:flex;justify-content:" + (fromMe ? "flex-end" : "flex-start") + ";margin:6px 0;box-sizing:border-box;";
    const bubble = document.createElement("div");
    bubble.style.cssText = "max-width:85%;min-width:120px;padding:10px 12px;border-radius:8px;box-sizing:border-box;text-align:left;" +
      (fromMe ? "background:#dcf8c6;" : "background:#f0f0f0;");
    const dateStr = msg.createdAt ? format(parseISO(msg.createdAt), "dd/MM/yyyy HH:mm") : "";
    const fromLabel = fromMe ? "Atendente" : "Contato";
    const body = (msg.body || "").replace(/</g, "&lt;").replace(/>/g, "&gt;").substring(0, 2000);
    const mediaUrl = msg.mediaUrl || (msg.media && msg.media.url) || "";
    const fullUrlForMedia = mediaUrl ? getMediaFullUrl(mediaUrl) : "";
    const linkBlock = (label, url) => {
      const safe = (url || "").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `<div style="margin:6px 0 4px 0;padding:6px 8px;background:#f0f7ff;border-radius:4px;border:1px solid #c5d9ed;"><span style="font-size:11px;font-weight:600;">${label}</span><br/><a href="${safe}" class="pdf-export-link" data-url="${safe}" style="font-size:11px;color:#1565c0;text-decoration:underline;word-break:break-all;min-height:18px;display:inline-block;">${url || ""}</a></div>`;
    };
    let content = `<div style="font-size:10px;color:#666;margin-bottom:4px;">${fromLabel} · ${dateStr}</div>`;
    const displayUrl = fullUrlForMedia || mediaUrl || "";
    if (mediaUrl && isImageMessage(msg)) {
      content += `<div style="margin:6px 0;"><img src="${fullUrlForMedia}" alt="" style="max-width:100%;max-height:280px;display:block;border-radius:6px;" crossorigin="anonymous" onerror="this.style.display='none';this.nextElementSibling.style.display='block';" /><span style="display:none;font-size:11px;color:#888;">[Imagem não disponível]</span></div>`;
      if (displayUrl) content += linkBlock("Link da imagem:", displayUrl);
    }
    if (mediaUrl && !isImageMessage(msg)) {
      const label = getMediaLabel(msg);
      if (displayUrl) content += linkBlock(label, displayUrl);
    }
    content += `<div style="font-size:12px;word-break:break-word;">${body || (mediaUrl ? "" : "[mídia ou sem texto]")}</div>`;
    bubble.innerHTML = content;
    row.appendChild(bubble);
    return row;
  };

  /** Coleta posições dos links no wrapper (para adicionar links clicáveis no jsPDF) */
  const collectLinkRects = (wrapper) => {
    const wrapperRect = wrapper.getBoundingClientRect();
    const links = [];
    wrapper.querySelectorAll("a.pdf-export-link").forEach((el) => {
      const r = el.getBoundingClientRect();
      links.push({
        url: el.getAttribute("data-url") || el.href || "",
        x: r.left - wrapperRect.left,
        y: r.top - wrapperRect.top,
        w: r.width,
        h: r.height,
      });
    });
    return links;
  };

  /** Gera PDF a partir de uma lista de mensagens (mesmo layout para "em tela" e "todo histórico"). */
  const generatePdfFromMessages = async (messages) => {
    if (!messages.length) {
      toastError("Nenhuma mensagem para exportar.");
      return;
    }
    const contactName = (ticketProp && ticketProp.contact && ticketProp.contact.name) ? ticketProp.contact.name : "Contato";
    const contactNumber = (ticketProp && ticketProp.contact && ticketProp.contact.number) ? String(ticketProp.contact.number).trim() : "";
    const contactLine = contactNumber ? `${contactNumber} - ${contactName}` : contactName;
    const headerHtml = document.createElement("div");
    headerHtml.style.cssText = "width:100%;padding:12px;background:#075e54;color:#fff;border-radius:8px;margin-bottom:12px;box-sizing:border-box;";
    headerHtml.innerHTML = `<strong>Atendimento #${ticketId}</strong><br/>${contactLine}`;

    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageW = 210;
    const pageH = 297;
    const scale = 1.5;
    let firstPage = true;
    let msgIndex = 0;

    while (msgIndex < messages.length) {
      const wrapper = document.createElement("div");
      wrapper.style.cssText = `width:${PAGE_CONTENT_WIDTH}px;min-width:${PAGE_CONTENT_WIDTH}px;max-width:${PAGE_CONTENT_WIDTH}px;background:#fff;padding:16px;box-sizing:border-box;`;
      if (firstPage) wrapper.appendChild(headerHtml.cloneNode(true));
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-10000px;top:0;z-index:9999;pointer-events:none;";
      container.appendChild(wrapper);
      document.body.appendChild(container);

      while (msgIndex < messages.length) {
        const msg = messages[msgIndex];
        const prevMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;
        const isNewTicket = !prevMsg || (msg.ticketId ?? ticketId) !== (prevMsg.ticketId ?? ticketId);
        if (isNewTicket) {
          const tid = msg.ticketId ?? msg.ticket?.id ?? ticketId;
          const isCurrentTicket = ticketProp && (ticketProp.id === tid || ticketProp.uuid === tid);
          const ticket = isCurrentTicket ? ticketProp : (msg.ticket || null);
          const ticketHasNoQueue = !ticket || ticket.queueId === null || ticket.queueId === undefined || !(ticket.queue && ticket.queue.name);
          const queueName = ticketHasNoQueue ? "Sem Fila" : (ticket.queue && ticket.queue.name ? String(ticket.queue.name).trim() : "Sem Fila");
          const separatorId = msg.ticketId ?? (msg.ticket && msg.ticket.id) ?? ticketId;
          wrapper.appendChild(createChamadoSeparatorNode(separatorId, queueName));
        }
        wrapper.appendChild(messageToHtmlNode(msg));
        msgIndex++;
        if (wrapper.offsetHeight >= MAX_CHUNK_HEIGHT_PX) break;
      }

      await waitForImages(wrapper);
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => setTimeout(r, 100));
      const linkRects = collectLinkRects(wrapper);
      const wrapperW = wrapper.offsetWidth || PAGE_CONTENT_WIDTH;
      const scaleX = pageW / wrapperW;
      const scaleY = pageH / (wrapper.offsetHeight || 400);

      const canvas = await html2canvas(wrapper, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });
      if (container.parentNode) document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      if (!firstPage) doc.addPage();
      doc.addImage(imgData, "JPEG", 0, 0, pageW, pageH);
      linkRects.forEach(({ url, x, y, w, h }) => {
        if (url) {
          try {
            doc.link(x * scaleX, y * scaleY, w * scaleX, h * scaleY, { url });
          } catch (e) {
            console.warn("Link não adicionado:", e);
          }
        }
      });
      firstPage = false;
      setExportProgress(messages.length ? Math.min(100, Math.round((msgIndex / messages.length) * 100)) : 100);
    }

    doc.save(`relatório_atendimento_${ticketId}.pdf`);
    setExportProgress(100);
    toast.success("PDF exportado com sucesso.");
    handleClose();
  };

  /** Exporta todo o histórico: busca todas as mensagens e gera PDF com o mesmo layout. */
  const handleExportFullHistory = async () => {
    setExportingPdf(true);
    setExportProgress(0);
    try {
      const messages = await fetchAllMessages();
      await generatePdfFromMessages(messages);
    } catch (err) {
      console.error(err);
      toastError(err?.message || "Erro ao exportar PDF.");
    } finally {
      setExportingPdf(false);
      setExportProgress(0);
    }
  };

  /** Exporta apenas o que está em tela: primeira página da API, mesmo layout (alinhamento, separador, links). */
  const handleExportToPDF = async () => {
    setExportingPdf(true);
    setExportProgress(0);
    try {
      const messages = await fetchFirstPageMessages();
      await generatePdfFromMessages(messages);
    } catch (err) {
      console.error(err);
      toastError(err?.message || "Erro ao exportar PDF.");
    } finally {
      setExportingPdf(false);
      setExportProgress(0);
    }
  };

  const handleExportAndClose = async () => {
    if (!exportedToPDF) {
      setExportedToPDF(true);
      await handleExportToPDF();
      handleClose();
    }
  };

  useEffect(() => {
    if (open && !exportedToPDF) {
      if (exportMode === "fullHistory") {
        handleExportFullHistory();
      } else {
        handleExportAndClose();
      }
    }
  }, [open, ticketId, exportMode]);

  return (
    <Dialog maxWidth="sm" onClose={handleClose} open={open}>
      <DialogActions style={{ flexWrap: "wrap" }}>
        <Button
          onClick={handleExportToPDF}
          color="primary"
          disabled={exportingPdf}
          startIcon={exportingPdf ? <CircularProgress size={20} /> : null}
        >
          {exportingPdf ? `Exportando... ${exportProgress}%` : "Exportar para PDF"}
        </Button>
        <Button onClick={handleClose} color="primary" disabled={exportingPdf}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
